import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import EscrowTracker from "@/components/EscrowTracker";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Disclaimer } from "@/components/Disclaimers";
import { Plane, BedDouble, Stethoscope, Loader2, Lock, ArrowRightCircle, MapPin, Calendar, Video, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";

const money = (n) => `$${Number(n).toLocaleString(undefined, { maximumFractionDigits: 2 })}`;
const ICON = { flight: Plane, accommodation: BedDouble, appointment: Stethoscope };

export default function Journey() {
  const { id } = useParams();
  const { user } = useAuth();
  const [itin, setItin] = useState(null);
  const [escrow, setEscrow] = useState(null);
  const [advancing, setAdvancing] = useState(false);
  const [teles, setTeles] = useState([]);
  const [teleAt, setTeleAt] = useState("");
  const [teleNotes, setTeleNotes] = useState("");
  const [scheduling, setScheduling] = useState(false);

  const load = async () => {
    const [a, b] = await Promise.all([
      api.get(`/v1/itineraries/${id}`),
      api.get(`/v1/escrow/${id}`),
    ]);
    setItin(a.data);
    setEscrow(b.data);
    api.get("/v1/teleconsult/mine").then((r) => setTeles(r.data.filter((t) => t.itinerary_id === id))).catch(() => {});
  };

  useEffect(() => { load().catch(() => toast.error("Could not load journey")); /* eslint-disable-next-line */ }, [id]);

  const advance = async () => {
    setAdvancing(true);
    try {
      await api.post(`/v1/escrow/${id}/advance`);
      toast.success("Milestone advanced · escrow released");
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not advance");
    } finally { setAdvancing(false); }
  };

  const scheduleTele = async () => {
    if (!teleAt) { toast.error("Pick a date & time"); return; }
    setScheduling(true);
    try {
      await api.post("/v1/teleconsult/schedule", { itinerary_id: id, scheduled_at: teleAt, notes: teleNotes });
      toast.success("Teleconsultation scheduled");
      setTeleAt(""); setTeleNotes("");
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not schedule");
    } finally { setScheduling(false); }
  };

  if (!itin || !escrow) return <div className="min-h-[60vh] grid place-items-center text-muted-foreground"><Loader2 className="animate-spin" /></div>;

  const canAdvance = (user.role === "provider" || user.role === "admin") && itin.payment_status === "paid" && itin.escrow_status !== "COMPLETED";
  const rel = escrow.released;

  return (
    <div className="max-w-5xl mx-auto px-5 sm:px-8 py-8">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="font-heading text-2xl font-bold text-[#101828]">{itin.provider_name}</h1>
          <p className="text-sm text-muted-foreground flex items-center gap-1"><MapPin size={13} /> {itin.package?.name} · {itin.accommodation?.city}</p>
        </div>
        <div className="flex items-center gap-2">
          <Badge className={itin.payment_status === "paid" ? "bg-[#12B76A] text-white border-0" : "bg-[#F79009] text-white border-0"} data-testid="payment-badge">
            {itin.payment_status === "paid" ? "Paid" : "Payment pending"}
          </Badge>
          <Badge variant="outline" className="border-[#0D5C75]/30 text-[#0D5C75]" data-testid="escrow-status-badge">{itin.escrow_status}</Badge>
        </div>
      </div>

      {/* Escrow */}
      <div className="mt-6 rounded-2xl border border-[#E4E7EC] bg-white p-6">
        <div className="flex items-center gap-2 mb-6"><Lock size={18} className="text-[#0D5C75]" /><h2 className="font-heading font-bold text-[#101828]">Escrow milestones</h2></div>
        <EscrowTracker status={itin.escrow_status} />
        <div className="mt-8 grid sm:grid-cols-3 gap-4">
          <Stat label="Total Contract Value" value={money(escrow.split.tcv)} />
          <Stat label="Released to providers" value={money(rel.total_released)} accent="#12B76A" />
          <Stat label="Held in escrow" value={money(rel.total_held)} accent="#F79009" />
        </div>

        <div className="mt-6 rounded-xl bg-[#F7F9FC] border border-[#E4E7EC] p-4 text-sm">
          <div className="font-medium text-[#101828] mb-2">Payout breakdown</div>
          <div className="grid sm:grid-cols-2 gap-y-1.5 gap-x-6">
            <Line label="Medical payout (85%)" total={escrow.split.medical_payout} released={rel.medical_released} />
            <Line label="Accommodation payout (88%)" total={escrow.split.accommodation_payout} released={rel.accommodation_released} />
            <Line label="Travel payout (97%)" total={escrow.split.travel_payout} released={rel.travel_released} />
            <Line label="Platform fee" total={escrow.split.platform_fee} released={rel.platform_released} />
            {escrow.split.travel_commission > 0 && <Line label="Travel affiliate commission (3%)" total={escrow.split.travel_commission} released={escrow.split.travel_commission} />}
            {escrow.split.insurance_price > 0 && <Line label="Travel insurance (platform 15%)" total={escrow.split.insurance_platform} released={escrow.split.insurance_platform} />}
            {escrow.split.concierge_price > 0 && <Line label="Concierge (platform 100%)" total={escrow.split.concierge_platform} released={escrow.split.concierge_platform} />}
          </div>
        </div>

        {canAdvance && (
          <Button onClick={advance} disabled={advancing}
            className="mt-5 rounded-full bg-[#0D5C75] text-white hover:bg-[#094A5E] hover:text-white" data-testid="advance-milestone-btn">
            {advancing ? <Loader2 className="animate-spin mr-2" size={16} /> : <ArrowRightCircle size={16} className="mr-1.5" />}
            Advance to next milestone
          </Button>
        )}
      </div>

      {/* Timeline */}
      <div className="mt-6 rounded-2xl border border-[#E4E7EC] bg-white p-6">
        <div className="flex items-center gap-2 mb-5"><Calendar size={18} className="text-[#0D5C75]" /><h2 className="font-heading font-bold text-[#101828]">Unified itinerary</h2></div>
        <div className="relative pl-6">
          <div className="absolute left-[9px] top-1 bottom-1 w-0.5 bg-[#E4E7EC]" />
          {itin.timeline.map((t, i) => {
            const Icon = ICON[t.type] || Stethoscope;
            return (
              <div key={i} className="relative pb-6 last:pb-0" data-testid={`timeline-item-${i}`}>
                <span className="absolute -left-6 grid place-items-center h-5 w-5 rounded-full bg-[#0D5C75] text-white"><Icon size={11} /></span>
                <div className="flex justify-between items-start">
                  <div>
                    <div className="font-medium text-[#101828]">{t.title}</div>
                    <div className="text-sm text-muted-foreground">{t.detail}</div>
                  </div>
                  <div className="text-xs text-muted-foreground whitespace-nowrap">{t.date}</div>
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Follow-up teleconsultation (patient, completed journeys) */}
      {user.role === "patient" && itin.escrow_status === "COMPLETED" && (
        <div className="mt-6 rounded-2xl border border-[#E4E7EC] bg-white p-6" data-testid="teleconsult-card">
          <div className="flex items-center gap-2 mb-2"><Video size={18} className="text-[#0D5C75]" /><h2 className="font-heading font-bold text-[#101828]">Schedule a follow-up teleconsultation</h2></div>
          <p className="text-sm text-muted-foreground mb-4">Reconnect with {itin.provider_name} for a post-treatment video review. <span className="font-medium text-[#101828]">$50 per session.</span></p>

          {teles.length > 0 && (
            <div className="space-y-2 mb-4">
              {teles.map((t) => (
                <div key={t.id} className="flex items-center justify-between rounded-xl border border-[#E4E7EC] bg-[#F7F9FC] p-3 text-sm" data-testid={`tele-${t.id}`}>
                  <span className="flex items-center gap-2 text-[#101828]"><Calendar size={14} className="text-[#0D5C75]" /> {new Date(t.scheduled_at).toLocaleString()}</span>
                  <Badge className={t.status === "completed" ? "bg-[#12B76A] text-white border-0" : "bg-[#0D5C75] text-white border-0"}>
                    {t.status === "completed" ? <CheckCircle2 size={12} className="mr-1" /> : null}{t.status}
                  </Badge>
                </div>
              ))}
            </div>
          )}

          <div className="grid sm:grid-cols-2 gap-3">
            <div>
              <label className="text-sm text-muted-foreground">Preferred date & time</label>
              <Input type="datetime-local" value={teleAt} onChange={(e) => setTeleAt(e.target.value)} className="mt-1.5" data-testid="tele-datetime" />
            </div>
            <div>
              <label className="text-sm text-muted-foreground">Notes (optional)</label>
              <Textarea value={teleNotes} onChange={(e) => setTeleNotes(e.target.value)} className="mt-1.5" placeholder="What would you like to discuss?" data-testid="tele-notes" />
            </div>
          </div>
          <Button onClick={scheduleTele} disabled={scheduling}
            className="mt-4 rounded-full bg-[#0D5C75] text-white hover:bg-[#094A5E] hover:text-white" data-testid="schedule-tele-btn">
            {scheduling ? <Loader2 className="animate-spin mr-2" size={16} /> : <Video size={16} className="mr-1.5" />}
            Book teleconsultation · $50
          </Button>
          <Disclaimer testid="teleconsult-disclaimer" className="mt-3">
            This follow-up consultation is provided directly by the independent healthcare provider, not by SarvaSeva, and is for post-treatment guidance only. It is not a diagnosis, prescription, or a substitute for in-person or emergency care. In a medical emergency, contact local emergency services immediately.
          </Disclaimer>
        </div>
      )}
    </div>
  );
}

function Stat({ label, value, accent = "#0D5C75" }) {
  return (
    <div className="rounded-xl border border-[#E4E7EC] p-4">
      <div className="text-xs text-muted-foreground">{label}</div>
      <div className="font-heading text-xl font-extrabold mt-1" style={{ color: accent }}>{value}</div>
    </div>
  );
}

function Line({ label, total, released }) {
  return (
    <div className="flex justify-between">
      <span className="text-muted-foreground">{label}</span>
      <span className="text-[#101828]">${released.toLocaleString()} <span className="text-muted-foreground">/ ${total.toLocaleString()}</span></span>
    </div>
  );
}
